<template>
  <div />
</template>

<script>

/**
 * Vue: Klaviyo (analytics)
 * -----------------------------------------------------------------------------
 * Klaviyo custom tracking
 *
 */
import { mapState } from 'vuex'
import { formatProduct, formatProductTitle } from '~/helpers/format'
import { getVariables } from '~/helpers/graphql'
import productsQuery from '~graphql/products.gql'

export default {
  // eslint-disable-next-line vue/multi-word-component-names
  name: 'Klaviyo',

  data() {
    return {
      ready: false,
      cache: {},
    }
  },

  computed: {

    /**
     * Map Vuex state.
     */
    ...mapState({
      cart: (state) => state.cart,
    }),

    /**
     * Compute all the collections that the cart items are in
     * @returns {String[]}
     */
    collections() {
      const allowedCollections = this.$variable('metafields.klaviyo.addToCartCollections')
      const collections = []
      this.cart?.response?.items.forEach(item => {
        const cachedItem = this.cache[String(item.product_id)]
        if (!cachedItem) {
          return
        }
        cachedItem.collections.forEach(collection => {
          if (
            collections.includes(collection.title) ||
            !allowedCollections.includes(collection.title.toLowerCase())
          ) {
            return
          }
          collections.push(collection.title)
        })
      })
      collections.sort()
      return collections
    },
  },

  watch: {

    /**
     * Watch ready state to identify user
     * @param state
     */
    ready(state) {
      if (!state) {
        return
      }
      this.identify()
    },

    /**
     * Watch for when items are added to cart to track add to cart
     * @param newCart
     * @param oldCart
     */
    async 'cart.response'(newCart, oldCart) {
      if (newCart.item_count <= oldCart.item_count || !this.ready) {
        return
      }

      const addedItems = newCart.items.filter(newItem => {
        const item = oldCart.items.find(oldItem => {
          return oldItem.id === newItem.id
        })
        return item ? item.quantity !== newItem.quantity : true
      })

      if (!addedItems.length) {
        return
      }

      const variants = newCart.items.map(item => {
        return `${item.variant_id}:${item.quantity}`
      })
      const permalink = `${window.origin}${this.$variable('routes.normalisedRoot')}cart/${variants.join(',')}`
      await this.updateProductCache(newCart.items)

      window.klaviyo.track('Added to Cart', {
        $currency_code: newCart.currency,
        $value: newCart.total_price / 100,
        Collections: this.collections,
        cart_items: newCart.items.map(this.formatLineItem),
        extra: {
          line_items: addedItems.map(this.formatLineItem),
          responsive_checkout_url: permalink,
        },
        Items: newCart.items.map(item => {
          return formatProductTitle(item.product_title)
        }),
      })
    },
  },

  created() {
    try {
      this.cache = JSON.parse(sessionStorage.getItem('klaviyoCartCache')) || {}
    } catch {
      this.cache = {}
    }
    this.awaitKlaviyo()
    this.setEventListeners()
  },

  methods: {

    /**
     * Set ready state when Klaviyo is available in window object.
     * @param retries
     */
    awaitKlaviyo(retries = 0) {
      if (window.klaviyo) {
        this.ready = true
        return
      }
      if (retries >= 25) {
        return
      }
      setTimeout(() => {
        this.awaitKlaviyo(retries + 1)
      }, 200)
    },

    /**
     * Cache product data from Shopify's product AJAX API
     * @param items
     * @returns
     */
    async updateProductCache(items) {
      try {
        const ids = []

        items.forEach(item => {
          const id = String(item.product_id)
          if (!id || this.cache[id]) {
            return
          }
          ids.push(`gid://shopify/Product/${id}`)
        })

        if (!ids.length) {
          return
        }

        const response = await cnvs.Query({
          query: productsQuery,
          variables: getVariables({
            ids,
          }),
        })

        response.nodes.forEach(product => {
          const formatted = formatProduct({ product })
          if (!formatted?.id) {
            return
          }
          this.cache[String(formatted.id)] = formatted
        })

        sessionStorage.setItem('klaviyoCartCache', JSON.stringify(this.cache))
      } catch (error) {
        cnvs.ReportError('[Klaviyo Product Cache]', error)
      }
    },

    /**
     * Identify user for Klaviyo analytics
     * @param {function} callback - Identify complete callback
     */
    // eslint-disable-next-line no-empty-function
    identify(callback = () => {}) {
      const email = this.$variable('customer.email')
      if (!email || !this.ready) {
        return
      }

      window.klaviyo.identify({ email }, callback)
    },

    /**
     * Format cart line items
     * @param item - Cart line item
     * @returns {Object}
     */
    formatLineItem(item) {
      const prices = [
        'compare_at_price',
        'compare_at_line_price',
        'discounted_price',
        'final_line_price',
        'final_price',
        'line_level_total_discount',
        'line_price',
        'original_line_price',
        'original_price',
        'price',
        'total_discount',
      ]

      const cachedItem = this.cache[String(item.product_id)]

      const featuredImage = item.featured_image || { url: item.image }
      const images = cachedItem
        ? cachedItem.media.map(mediaItem => mediaItem.image)
        : [
          {
            ...featuredImage,
            src: featuredImage.url,
          },
        ]

      const title = formatProductTitle(item.title)
      const payload = {
        ...item,
        title: formatProductTitle(item.title),
        url: new URL(item.url, window.origin).href,
        product: {
          title,
          body_html: item.product_description,
          images,
        },
      }

      prices.forEach(key => {
        const value = item[key]
        if (!value || typeof value !== 'number') {
          return
        }
        payload[key] = value / 100
      })

      return payload
    },

    /**
     * Set event listeners
     */
    setEventListeners() {
      cnvs.EventBus.listen('klaviyo:identify', this.handleIdentifyEvent)
    },

    /**
     * Handle klaviyo:identify event
     * @param {string} email
     * @param {function} afterIdentify
     */
    handleIdentifyEvent({ email, afterIdentify }) {
      if (!cnvs.customer.email) {
        sessionStorage.setItem('customerEmail', email)
        cnvs.customer.email = email
      }

      let callback = afterIdentify

      if (!callback) {
        // eslint-disable-next-line no-empty-function
        callback = () => {}
      }

      this.identify(callback)
    },
  },
}
</script>
